import React from 'react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-navy text-[22px] font-black tracking-tight">{title}</h2>
    <div className="mt-3 space-y-3 text-body-muted text-[15px] leading-[1.8]">{children}</div>
  </section>
);

const SubHeading = ({ children }) => (
  <p className="text-navy font-bold mt-4">{children}</p>
);

const Terms = () => {
  return (
    <div className="min-h-screen bg-white font-sans text-navy">
      <Navbar />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">Legal</p>
          <h1 className="text-navy font-black tracking-[-0.01em] leading-[1.08] text-[36px] sm:text-[44px]">Terms of Use</h1>
          <p className="mt-4 text-body-muted text-[14px]">Last updated: 17 August 2026</p>

          <Section title="1. Acceptance of Terms">
            <p>By accessing or using the 5DBS website (including all content, resources, reports, and downloadable materials), you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our website.</p>
            <p>5DBS reserves the right to modify these terms at any time. We will update the "Last updated" date above. Your continued use of the website after changes constitutes your acceptance of the new terms.</p>
          </Section>

          <Section title="2. Use License">
            <p>5DBS grants you a limited, non-exclusive, non-transferable license to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>View and access our website</li>
              <li>Download resources and reports (for your personal or internal business use)</li>
              <li>Contact us with inquiries</li>
            </ul>
            <SubHeading>You may not:</SubHeading>
            <ul className="list-disc pl-5 space-y-1">
              <li>Reproduce, distribute, or publicly display any content without permission</li>
              <li>Modify, adapt, or create derivative works from our content</li>
              <li>Use our content for commercial purposes (selling reports, reselling insights, etc.)</li>
              <li>Remove or alter copyright, trademark, or proprietary notices</li>
              <li>Use automated tools (bots, scrapers, crawlers) to collect data from our website</li>
              <li>Reverse engineer or attempt to extract our methodologies, models, or data</li>
              <li>Use our content to compete with 5DBS or harm our business</li>
            </ul>
            <p>Unauthorized use may result in legal action and termination of your access.</p>
          </Section>

          <Section title="3. Intellectual Property">
            <SubHeading>Our content</SubHeading>
            <ul className="list-disc pl-5 space-y-1">
              <li>All content on the 5DBS website (text, graphics, logos, reports, whitepapers, methodologies) is owned by 5DBS or our licensors.</li>
              <li>We retain all intellectual property rights. No rights are implied by downloading or viewing content.</li>
              <li>"5DBS" and related logos are trademarks of 5DBS. Unauthorized use is prohibited.</li>
            </ul>
            <SubHeading>Your content</SubHeading>
            <ul className="list-disc pl-5 space-y-1">
              <li>Any feedback, suggestions, or information you send us (via contact forms, emails, etc.) becomes the property of 5DBS. We may use it without attribution or compensation.</li>
              <li>You warrant that any content you submit does not infringe third-party rights and that you have the right to grant us these permissions.</li>
            </ul>
          </Section>

          <Section title="4. Disclaimers">
            <SubHeading>4.1 "As-Is" Basis</SubHeading>
            <p>The website and all content are provided "as-is" without warranties of any kind, express or implied. 5DBS disclaims all warranties including:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Accuracy, completeness, or reliability of any information</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement of third-party rights</li>
              <li>Uninterrupted or error-free operation</li>
            </ul>
            <SubHeading>4.2 No Professional Advice</SubHeading>
            <p>Content on our website (reports, whitepapers, case studies, data) is for informational purposes only. It is not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Legal, financial, or business advice</li>
              <li>A recommendation to take specific action</li>
              <li>A guarantee of results or outcomes</li>
              <li>A substitute for professional consultation</li>
            </ul>
            <p>You should consult qualified professionals (lawyers, accountants, business advisors) before making decisions based on our content.</p>
            <SubHeading>4.3 Third-Party Links</SubHeading>
            <p>Our website may contain links to third-party websites (Enrich IQ, AALIGN, partner sites, etc.). We do not endorse or control these sites. You access them at your own risk and are subject to their terms and privacy policies.</p>
            <SubHeading>4.4 No Guarantee of Results</SubHeading>
            <p>Case studies and examples in our content represent specific scenarios. Results vary widely depending on implementation, market conditions, and other factors. We do not guarantee that following our guidance will produce similar results.</p>
            <SubHeading>4.5 Forward-Looking Statements</SubHeading>
            <p>Our content may contain forward-looking statements about market trends, publisher revenue opportunities, and industry developments. These are predictions based on current information and may not come true. Market conditions change, and our analysis may become outdated.</p>
          </Section>

          <Section title="5. Limitation of Liability">
            <p>To the fullest extent permitted by law, 5DBS shall not be liable for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Any indirect, incidental, special, or consequential damages</li>
              <li>Lost profits, revenue, data, or business opportunity</li>
              <li>Damages arising from your use or inability to use our website or content</li>
              <li>Errors, omissions, or inaccuracies in our content</li>
              <li>Third-party conduct or services (including Enrich IQ, AALIGN, other partners)</li>
            </ul>
            <p>Our total liability for any claim arising from your use of this website shall not exceed the amount you paid us (if any). If you did not pay us, your remedy is limited to termination of access.</p>
            <p>This limitation applies even if:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>We have been advised of the possibility of such damages</li>
              <li>The limitation would otherwise be unenforceable under applicable law</li>
              <li>A limited remedy fails of its essential purpose</li>
            </ul>
          </Section>

          <Section title="6. Indemnification">
            <p>You agree to indemnify, defend, and hold harmless 5DBS and its officers, employees, and agents from any claims, damages, or costs (including legal fees) arising from:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Your use of our website or content</li>
              <li>Your violation of these terms</li>
              <li>Your infringement of third-party rights</li>
              <li>Any content you submit to us</li>
            </ul>
          </Section>

          <Section title="7. Separate Services — Enrich IQ & AALIGN">
            <p><strong>Important:</strong> Enrich IQ and AALIGN are separate products with separate terms of service and privacy policies.</p>
            <p>This Terms of Use applies only to the 5DBS website and resources. When you use Enrich IQ or AALIGN:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>You are subject to their separate terms of service</li>
              <li>Their privacy policies govern data processing</li>
              <li>Their liability limitations apply to those services</li>
              <li>5DBS is not responsible for their conduct or performance</li>
            </ul>
          </Section>

          <Section title="8. Prohibited Conduct">
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Violate any applicable law or regulation</li>
              <li>Harass, abuse, or threaten other users or 5DBS staff</li>
              <li>Transmit viruses, malware, or harmful code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with the operation or security of our website</li>
              <li>Engage in "phishing" or impersonation</li>
              <li>Send spam or unsolicited communications</li>
              <li>Collect or track personal information without consent</li>
            </ul>
            <p>Violation of this section may result in immediate termination of access and legal action.</p>
          </Section>

          <Section title="9. Termination of Access">
            <p>5DBS may terminate your access to the website at any time, with or without cause, without notice or liability. Reasons may include:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Violation of these terms</li>
              <li>Misuse of content or systems</li>
              <li>Harmful conduct toward 5DBS or other users</li>
              <li>Legal or regulatory requirements</li>
              <li>Our discretionary decision</li>
            </ul>
            <p>Upon termination, your right to access the website immediately ends.</p>
          </Section>

          <Section title="10. Governing Law & Dispute Resolution">
            <p>These Terms of Use are governed by the laws of England and Wales, without regard to conflicts of law principles.</p>
            <SubHeading>10.1 Informal Resolution</SubHeading>
            <p>Before taking legal action, you agree to attempt to resolve disputes by emailing us at <a href="mailto:p.patne@fivedbs.com" className="text-brand-blue font-semibold hover:underline">p.patne@fivedbs.com</a> with details of your claim. We will attempt to resolve the matter within 30 days.</p>
            <SubHeading>10.2 Jurisdiction & Courts</SubHeading>
            <p>Both you and 5DBS agree that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Any legal proceedings shall be brought exclusively in the courts of England and Wales</li>
              <li>You consent to the jurisdiction of these courts</li>
              <li>You waive any objection to venue or inconvenient forum</li>
            </ul>
            <SubHeading>10.3 Severability</SubHeading>
            <p>If any provision of these terms is found to be unenforceable, that provision shall be severed, and the remaining terms shall remain in full force.</p>
          </Section>

          <Section title="11. Entire Agreement">
            <p>These Terms of Use (together with our Privacy Policy and any separate service agreements) constitute the entire agreement between you and 5DBS regarding use of our website. They supersede all prior understandings and agreements, whether written or oral.</p>
          </Section>

          <Section title="12. Waiver">
            <p>Our failure to enforce any right or provision does not constitute a waiver of that right or provision. If we waive enforcement in one instance, it does not waive enforcement in future instances.</p>
          </Section>

          <Section title="13. Assignment">
            <p>You may not assign or transfer these terms or your rights under them without our written consent. Any unauthorized assignment is void. 5DBS may assign these terms at any time without notice.</p>
          </Section>

          <Section title="14. Contact & Support">
            <p>Questions about these terms?</p>
            <p><strong>Email:</strong> <a href="mailto:p.patne@fivedbs.com" className="text-brand-blue font-semibold hover:underline">p.patne@fivedbs.com</a></p>
            <p><strong>Mailing address:</strong></p>
            <p>5DBS<br />60 St Martins Lane<br />Covent Garden<br />London, WC2N 4JS<br />United Kingdom</p>
          </Section>

          {/* One-page summary card */}
          <div className="mt-16 rounded-2xl bg-navy-deep text-white p-8 shadow-mk-lg">
            <p className="text-brand-blue-light text-[11.5px] font-bold uppercase tracking-[0.18em] mb-3">Summary (for users)</p>
            <h3 className="text-white font-black text-[26px] leading-tight">The short version.</h3>

            <p className="mt-6 text-brand-blue-light text-[12px] font-bold uppercase tracking-[0.14em]">You can</p>
            <ul className="mt-2 space-y-2 text-slate-300 text-[14.5px] leading-[1.7]">
              <li>• Download and read our resources for your personal or internal business use</li>
              <li>• Share insights with your team</li>
              <li>• Use the information to inform business decisions</li>
              <li>• Contact us with questions</li>
            </ul>

            <p className="mt-6 text-brand-pink text-[12px] font-bold uppercase tracking-[0.14em]">You cannot</p>
            <ul className="mt-2 space-y-2 text-slate-300 text-[14.5px] leading-[1.7]">
              <li>• Resell our reports or content</li>
              <li>• Use our content to compete with 5DBS</li>
              <li>• Extract our data or methodologies</li>
              <li>• Republish our content without permission</li>
              <li>• Use bots or automated tools to scrape our site</li>
            </ul>

            <p className="mt-6 text-brand-pink text-[12px] font-bold uppercase tracking-[0.14em]">We're not liable for</p>
            <ul className="mt-2 space-y-2 text-slate-300 text-[14.5px] leading-[1.7]">
              <li>• Results you don't achieve using our guidance</li>
              <li>• Decisions you make based on our content (consult professionals)</li>
              <li>• Issues with third-party services (Enrich IQ, AALIGN, etc.)</li>
              <li>• Losses you suffer from using our website</li>
            </ul>

            <p className="mt-6 text-slate-400 text-[13px]">
              Last updated: 17 August 2026 · <a href="mailto:p.patne@fivedbs.com" className="text-brand-blue-light font-semibold hover:underline">p.patne@fivedbs.com</a>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;
